import { Volume2, VolumeX } from 'lucide-react';
import { useEffect, useId, useRef } from 'react';
import { useIntl } from 'react-intl';

import { DEVICE_ALERT_TOOLTIP_DELAY_MS } from '../constants/device-alerts.js';
import { unlockAlertSound } from '../lib/alert-sound-player.js';
import { useAlertSoundStore } from '../store/alertSound.js';
import { Button, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui';

export default function AlertSoundToggle() {
  const intl = useIntl();
  const enabled = useAlertSoundStore((state) => state.enabled);
  const blocked = useAlertSoundStore((state) => state.blocked);
  const isBlocked = enabled && blocked;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const blockedHintId = useId();

  // Capture phase on document: map and table handlers that stop propagation must not hide the gesture.
  useEffect(() => {
    if (!isBlocked) {
      return;
    }

    const unlockOnGesture = (event: Event) => {
      if (buttonRef.current?.contains(event.target as Node)) {
        return;
      }
      unlockAlertSound();
    };

    document.addEventListener('click', unlockOnGesture, true);
    document.addEventListener('keydown', unlockOnGesture, true);
    return () => {
      document.removeEventListener('click', unlockOnGesture, true);
      document.removeEventListener('keydown', unlockOnGesture, true);
    };
  }, [isBlocked]);

  // Read at click time: the store can change between this render and the click.
  const handleClick = () => {
    const { enabled: isEnabled, setEnabled } = useAlertSoundStore.getState();
    setEnabled(!isEnabled);
  };

  const blockedHint = intl.formatMessage({ id: 'nav.alertSound.blockedHint' });
  const tooltip = isBlocked
    ? blockedHint
    : intl.formatMessage({ id: enabled ? 'nav.alertSound.on' : 'nav.alertSound.off' });
  const Icon = enabled ? Volume2 : VolumeX;

  return (
    <TooltipProvider delayDuration={DEVICE_ALERT_TOOLTIP_DELAY_MS}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            ref={buttonRef}
            variant="ghost"
            size="sm"
            className="relative"
            onClick={handleClick}
            aria-pressed={enabled}
            aria-label={intl.formatMessage({ id: 'nav.alertSound.label' })}
            // Omitted, not undefined, when unblocked: an explicit prop overrides the tooltip's own.
            {...(isBlocked && { 'aria-describedby': blockedHintId })}
            data-test-id="alert-sound-toggle"
          >
            <Icon className="h-4 w-4" />
            {isBlocked && (
              <>
                <span
                  aria-hidden="true"
                  data-testid="alert-sound-blocked-dot"
                  className="absolute right-1 top-1 size-2 rounded-full bg-orange"
                />
                <span id={blockedHintId} className="sr-only">
                  {blockedHint}
                </span>
              </>
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
